import { APIS } from 'config';
import * as path from '../../utils/path';
import { handleLongPullResponse } from '../SspmComponent/Pages/Risks/SspmTable/TableActions/hooks/HandleLongPullResponse';
import {
  IntegrationKeysDto,
  IntegrationSettingsDto,
  IntegrationTenantSettingsDto
} from './ThirdPartyIntegration/types';
import { boolean, object, string } from 'yup';
import { AppConfigurationViewModel } from 'pages/Sspm/viewmodels/AppConfiguration';

const getResultPollingSchema = object({
  success: boolean().required(),
  error: string().optional()
});

export const deleteCurrentScanGroup = (tenantGuid: string) => {
  return fetch(path.join(APIS.SSPM, '/api/remove-scan-group'), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify({ tenantGuid })
  });
};

export const fetchActiveScanGroups = async (tenantGuid: string) => {
  const response = await fetch(
    path.join(APIS.SSPM, '/api/get-active-scan-group?' + new URLSearchParams({ tenantGuid })),
    {
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    }
  );
  return await response.json();
};

export const updateActiveScanGroup = async (tenantGuid: string, scanGroupId: number) => {
  const response = await fetch(path.join(APIS.SSPM, '/api/set-active-scan-group'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify({ groupId: scanGroupId, tenantGuid })
  });
  return await response.json();
};

export const fetchExternalIntegrationsTenantSettings = async (tenantId: string) => {
  const response = await fetch(
    path.join(APIS.APP, '/ExternalIntegration/GetTenantSettings?' + new URLSearchParams({ tenantId })),
    {
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    }
  );
  return await response.json();
};

export async function updateUserInfo(params: { tenantGuid: string; displayTrialAlert: boolean }) {
  const response = await fetch(`${APIS.SSPM}/api/update-user-info`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify(params)
  });
  if (!response.ok) {
    throw new Error(`Failed to update user info. Status: ${response.status}`);
  }
  return await response.json();
}

export const testApplicationConnectionWithTask = async (tenantGuid: string, appId: number) => {
  try {
    let isSuccess = false;

    const response = await fetch(`${APIS.SSPM}/api/test-app-connection-task`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify({ tenantGuid, appId })
    });

    if (!response.ok) {
      console.error('Failed to test application connection. Status: ', response.status);
      return false;
    }

    await handleLongPullResponse(response, tenantGuid, {
      onSuccess: async taskId => {
        const getResponseRequest = await fetch(
          `${APIS.SSPM}/api/get-result-test-app-connection?taskId=${taskId}&tenantGuid=${tenantGuid}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json;charset=utf-8'
            }
          }
        );
        if (!getResponseRequest.ok) {
          throw new Error(`Failed to test application connection. Status: ${response.status}`);
        }
        const { success, error } = getResultPollingSchema.validateSync(await getResponseRequest.json());

        if (success) {
          isSuccess = true;
        } else {
          console.error(error);
        }
      },
      onError: error => {
        console.error(error);
        return;
      }
    });
    return isSuccess;
  } catch (e) {
    throw e;
  }
};
export async function fetchUserInfo(tenantGuid: string) {
  const response = await fetch(`${APIS.SSPM}/api/get-user-info?tenantGuid=${tenantGuid}`, {
    method: 'GET'
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch user info. Status: ${response.status}`);
  }
  return await response.json();
}

export async function fetchGlobalIntegrationApps(tenantGuid: string): Promise<AppConfigurationViewModel[]> {
  let result: AppConfigurationViewModel[] = [];
  try {
    const response = await fetch(
      `${APIS.SSPM}/api/get-global-integration-apps?tenantGuid=${tenantGuid}&includeAppToggle=true`,
      {
        headers: {
          'Content-Type': 'application/json;charset=utf-8'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch global integration apps. Status: ${response.status}`);
    }

    result = await response.json();
  } catch (e) {
    reportError(e);
  }

  return result;
}

export const saveOrUpdateExternalIntegrationsTenantSettings = async (
  IntegrationTenantSettingsDto: IntegrationTenantSettingsDto
) => {
  const response = await fetch(path.join(APIS.APP, '/ExternalIntegration/SaveTenantSettings'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify(IntegrationTenantSettingsDto)
  });
  return await response.json();
};

export const getExternalIntegrationAdditionalInfo = async (IntegrationSettingsDto: IntegrationSettingsDto) => {
  const response = await fetch(path.join(APIS.APP, '/ExternalIntegration/GetAdditionalInfo'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify(IntegrationSettingsDto)
  });
  return await response.json();
};

export const openExternalIntegrationTestTicket = async (IntegrationKeysDto: IntegrationKeysDto) => {
  const response = await fetch(path.join(APIS.APP, '/ExternalIntegration/OpenTestTicket'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify({ ...IntegrationKeysDto })
  });
  return await response.json();
};

export const fetchAPIVersion = async (appId: string, tenantGuid: string): Promise<{ isAppToggle: boolean }> => {
  const response = await fetch(
    `${APIS.SSPM}/api/is-app-toggle?` +
      new URLSearchParams({
        appId,
        tenantGuid
      }),
    {
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch API version. Status: ${response.status}`);
  }
  return await response.json();
};

export const handleSwitchActionWithTask = async (
  tenantGuid: string,
  toggleId: number,
  isActionOn: boolean,
  shouldDisconnect = false
) => {
  try {
    let isSuccess = false;
    let errorMessage = '';
    let shouldHandleDisconnect = false;

    const response = await fetch(`${APIS.SSPM}/api/switch-toggle-action-task`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify({ actionId: toggleId, isActionOn, tenantGuid, shouldDisconnect })
    });

    await handleLongPullResponse(response, tenantGuid, {
      onSuccess: async taskId => {
        const getResponseRequest = await fetch(
          `${APIS.SSPM}/api/get-result-switch-toggle?taskId=${taskId}&tenantGuid=${tenantGuid}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json;charset=utf-8'
            }
          }
        );
        if (getResponseRequest.status === 409) {
          shouldHandleDisconnect = true;
          return;
        }
        if (!getResponseRequest.ok) {
          errorMessage = getResponseRequest.statusText;
          return;
        }
        const { success, error } = getResultPollingSchema.validateSync(await getResponseRequest.json());

        if (success) {
          isSuccess = true;
          return;
        } else {
          if (error) {
            errorMessage = error;
            return;
          }
        }
      },
      onError: error => {
        errorMessage = error;
        return;
      }
    });
    return {
      isSuccess,
      errorMessage,
      shouldHandleDisconnect
    };
  } catch (error: any) {
    const text = error.message || JSON.stringify(error);
    return { isSuccess: false, errorMessage: text, shouldHandleDisconnect: false };
  }
};

export async function disconnectApplicationWithTask(tenantGuid: string, appId: number) {
  try {
    let isSuccess = false;
    let errorMessage = '';

    const response = await fetch(
      path.join(`${APIS.SSPM}/api/disconnect-app-task?` + new URLSearchParams({ tenantGuid, appId: appId.toString() })),
      {
        headers: {
          'Content-Type': 'application/json;charset=utf-8'
        }
      }
    );

    await handleLongPullResponse(response, tenantGuid, {
      onSuccess: async taskId => {
        const getResponseRequest = await fetch(
          `${APIS.SSPM}/api/get-result-disconnect-app?taskId=${taskId}&tenantGuid=${tenantGuid}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json;charset=utf-8'
            }
          }
        );
        if (!getResponseRequest.ok) {
          errorMessage = getResponseRequest.statusText;
          return;
        }
        const { success, error } = getResultPollingSchema.validateSync(await getResponseRequest.json());

        if (success) {
          isSuccess = true;
          return;
        } else {
          if (error) {
            errorMessage = error;
            return;
          }
        }
      },
      onError: error => {
        errorMessage = error;
        return;
      }
    });
    return {
      isSuccess,
      errorMessage
    };
  } catch (error: any) {
    const text = error.message || JSON.stringify(error);
    return { isSuccess: false, errorMessage: text };
  }
}
